import React, {
  useState,
  useCallback,
  useEffect,
  useRef,
  useMemo,
} from "react";
import { Tooltip } from "@heroui/tooltip";
import { TripUser } from "@/types";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { debounce } from "lodash";
import { UserSearchResult } from "@/types";
import api from "@/config/axiosConfig";
import useAuthStore from "@/stores/user/authStore";
import { useConnections } from "@/hooks/useConnections";
import { AvatarImage } from "@/components/ui/DefaultAvatar";
import { useRateLimitStore } from "@/stores/rateLimitStore";

import { UsersRound, Trash, Check, Loader2 } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/hooks/use-toast";
import { UserPlusIcon } from "@heroicons/react/24/outline";
import tripService from "@/services/tripService";
import useTrip from "@/stores/tripBuilder/useTrip";
import { StarIcon } from "@heroicons/react/24/solid";
import logger from "@/utils/logger";
interface TripUsersProps {
  onRemoveUser: (user: TripUser) => void;
}

const TripUsers: React.FC<TripUsersProps> = React.memo(({ onRemoveUser }) => {
  const tripData = useTrip((state) => state.tripData);
  const setTripData = useTrip((state) => state.setTripData);
  const { user: currentUser, anonymousUser } = useAuthStore();
  const { setRateLimit } = useRateLimitStore();
  const searchContainerRef = useRef<HTMLDivElement>(null);
  const [searchResults, setSearchResults] = useState<UserSearchResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSharing, setIsSharing] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const { toast } = useToast();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const { data: connections, isLoading: isLoadingConnections } = useConnections(
    currentUser?.user_id || ""
  );
  const [selectedFriends, setSelectedFriends] = useState<Set<string>>(
    new Set()
  );

  // Define isActive at the component level - just as a placeholder for now
  // You should implement proper logic based on your application's requirements
  const isActive = true;

  const toggleFriendSelection = useCallback((user_id: string) => {
    setSelectedFriends((prev) => {
      const newSet = new Set(prev);
      newSet.has(user_id) ? newSet.delete(user_id) : newSet.add(user_id);
      return newSet;
    });
  }, []);

  const debouncedSearch = useCallback(
    debounce(async (query: string) => {
      if (!query.trim() || query.length < 3) {
        setSearchResults([]);
        return;
      }

      setIsLoading(true);
      try {
        const response = await api.social.get(
          `/search?q=${encodeURIComponent(query)}&friends=true`
        );
        setSearchResults(response.data.results);
      } catch (error) {
        console.error("Error searching users:", error);
      } finally {
        setIsLoading(false);
      }
    }, 300),
    []
  );

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const query = e.target.value;
      setSearchQuery(query);
      debouncedSearch(query);
    },
    [debouncedSearch]
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Don't handle clicks if they're on the dialog close button
      if (
        (event.target as Element)?.closest('[class*="DialogPrimitive-Close"]')
      ) {
        return;
      }

      if (
        searchContainerRef.current &&
        !searchContainerRef.current.contains(event.target as Node)
      ) {
        setSearchResults([]);
        setSearchQuery("");
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleShareTrip = useCallback(async () => {
    if (!tripData || selectedFriends.size === 0) return;

    setIsSharing(true);
    try {
      const { success, users: addedUsers } = await tripService.addTripUsers(
        tripData.id,
        Array.from(selectedFriends)
      );

      if (success && addedUsers) {
        setTripData({
          ...tripData,
          users: [
            ...tripData.users,
            ...addedUsers.map((user) => ({
              user_id: user.user_id,
              is_admin: true,
              username: user.username,
              first_name: user.first_name,
              last_name: user.last_name,
              profile_image: user.profile_image,
              creator: false,
            })),
          ],
        });

        toast({
          title: "Trip shared successfully",
          description: `Added ${addedUsers.length} user${
            addedUsers.length > 1 ? "s" : ""
          } to the trip`,
        });

        // close the dialog
        setIsDialogOpen(false);
        setSelectedFriends(new Set());
      }
    } catch (error) {
      console.error("Error sharing trip:", error);
      toast({
        title: "Failed to share trip",
        description: "There was an error sharing your trip. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSharing(false);
    }
  }, [tripData, setTripData, selectedFriends, toast]);

  const handleOpen = useCallback(() => {
    if (anonymousUser) {
      setRateLimit(
        "Create your account to share your trip with friends and family!"
      );
      return;
    }
    setIsDialogOpen(true);
  }, [anonymousUser, setRateLimit]);

  // Memoize sorted users to prevent unnecessary re-sorting on every render
  const sortedUsers = useMemo(() => {
    if (!tripData?.users) return [];

    return [...tripData.users].sort((a, b) => {
      if (a.creator) return -1;
      if (b.creator) return 1;
      if (a.user_id === currentUser?.user_id) return -1;
      if (b.user_id === currentUser?.user_id) return 1;
      return 0;
    });
  }, [tripData?.users, currentUser?.user_id]);

  // Memoize search results rendering
  const searchResultsContent = useMemo(() => {
    if (isLoading) {
      return Array(3)
        .fill(0)
        .map((_, index) => (
          <div key={index} className="flex items-center justify-between p-3">
            <div className="flex items-center space-x-3">
              <Skeleton className="w-8 h-8 rounded-full" />
              <div className="space-y-2">
                <Skeleton className="h-4 w-[120px]" />
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <Skeleton className="h-8 w-20" />
              <Skeleton className="h-7 w-7 rounded-full" />
            </div>
          </div>
        ));
    }

    if (searchQuery.length >= 3 && searchResults.length === 0) {
      return (
        <div className="text-center py-4 text-sm text-gray-500">
          No users found
        </div>
      );
    }

    return searchResults.map((user) => {
      const isAlreadyAdded = tripData?.users.some(
        (u) => u.user_id === user.user_id
      );
      return (
        <div
          key={user.user_id}
          className="w-full flex items-center justify-between p-3 hover:bg-slate-50 transition duration-200 cursor-pointer"
          onClick={() => {
            if (!isAlreadyAdded) {
              toggleFriendSelection(user.user_id);
            }
          }}
          role="button"
          tabIndex={0}
          aria-disabled={isAlreadyAdded}
        >
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 rounded-full bg-neutral-100 overflow-hidden">
              {user.profile_image && (
                <AvatarImage
                  src={user.profile_image}
                  className="w-full h-full object-cover"
                  firstName={user.first_name}
                  lastName={user.last_name}
                />
              )}
            </div>
            <p className="text-sm font-normal text-neutral-800">
              {user.first_name} {user.last_name}
            </p>
          </div>
          <div className="flex items-center space-x-2">
            <div
              className={`w-5 h-5 rounded-full flex items-center justify-center border-2 ${
                selectedFriends.has(user.user_id) || isAlreadyAdded
                  ? "bg-indigo-600 border-white"
                  : "bg-white border-neutral-300"
              } transition-colors duration-200`}
            >
              {(selectedFriends.has(user.user_id) || isAlreadyAdded) && (
                <Check className="w-4 h-2 text-white" />
              )}
            </div>
          </div>
        </div>
      );
    });
  }, [
    isLoading,
    searchQuery,
    searchResults,
    tripData?.users,
    selectedFriends,
    toggleFriendSelection,
  ]);

  // Memoize connections rendering
  const connectionsContent = useMemo(() => {
    if (!connections || connections.length === 0) return null;
    logger.log("connections", connections);

    return connections.map(
      (connection: {
        user_id: string;
        name: string;
        profile_image: string;
      }) => {
        const isAlreadyAdded = tripData?.users.some(
          (u) => u.user_id === connection.user_id
        );
        return (
          <div
            key={connection.user_id}
            className={`flex flex-col items-center space-y-2 min-w-[72px] relative group ${
              isAlreadyAdded ? "opacity-50" : ""
            } cursor-pointer`}
            onClick={() => {
              if (!isAlreadyAdded) {
                toggleFriendSelection(connection.user_id);
              }
            }}
            role="button"
            tabIndex={0}
            aria-disabled={isAlreadyAdded}
          >
            <div
              className={`w-12 h-12 rounded-full bg-gradient-to-tr transition-all duration-300 ease-in-out hover:scale-105 ${
                isAlreadyAdded || selectedFriends.has(connection.user_id)
                  ? "from-blue-500 to-indigo-500 scale-105"
                  : "from-blue-200 to-indigo-200 scale-100"
              } p-[2px]`}
            >
              <div className="w-full h-full rounded-full bg-white p-[2px] transition-transform duration-300">
                <div className="w-full h-full rounded-full overflow-hidden">
                  {connection.profile_image && (
                    <AvatarImage
                      src={connection.profile_image}
                      alt={`${connection.name}'s avatar`}
                      className="w-full h-full object-cover transition-transform duration-300"
                    />
                  )}
                </div>
              </div>
            </div>
            <div className="flex flex-col items-center">
              <span className="text-xs text-center truncate w-full font-medium">
                {connection.name.split(" ")[0]}
              </span>
              {connection.name.split(" ")[1] && (
                <span className="text-xs text-center truncate w-full text-neutral-500">
                  {connection.name.split(" ")[1]}
                </span>
              )}
            </div>
            <div
              className={`absolute -top-2 right-1 w-5 h-5 rounded-full flex items-center justify-center border-2 ${
                isAlreadyAdded || selectedFriends.has(connection.user_id)
                  ? "bg-indigo-600 border-white"
                  : "bg-white border-neutral-300"
              } transition-colors duration-200`}
            >
              {(isAlreadyAdded || selectedFriends.has(connection.user_id)) && (
                <Check className="w-4 h-2 text-white" />
              )}
            </div>
          </div>
        );
      }
    );
  }, [connections, tripData?.users, selectedFriends, toggleFriendSelection]);

  return (
    <div className="flex items-center" data-tutorial="trip-users">
      {sortedUsers.map((user, index) => {
        return (
          <Tooltip
            key={user.user_id}
            content={
              <div className="flex flex-col items-start space-y-3 font-light text-neutral-800 p-3 min-w-[200px]">
                <div className="flex items-center space-x-3 w-full">
                  <AvatarImage
                    src={user.profile_image}
                    firstName={user.first_name}
                    lastName={user.last_name}
                    className="object-cover w-12 h-12 2xl:w-[52px] 2xl:h-[52px]"
                  />
                  <div className="flex flex-col">
                    {user.first_name && user.last_name ? (
                      <p className="font-medium text-sm">
                        {user.first_name} {user.last_name}
                      </p>
                    ) : (
                      <p className="font-medium text-sm">Mystery Explorer</p>
                    )}
                    {user.username ? (
                      <p className="text-xs text-neutral-500">
                        @{user.username}
                      </p>
                    ) : (
                      <p className="text-xs text-neutral-500">
                        @unknownwanderer
                      </p>
                    )}
                  </div>
                </div>

                {user.creator && (
                  <div className="w-full flex items-center justify-center gap-1.5 rounded-lg bg-gradient-to-r from-amber-500/10 to-amber-600/10 px-3 py-2 text-xs font-medium text-amber-700 border border-amber-200/50">
                    <StarIcon className="w-3.5 h-3.5 text-amber-500" />
                    <span>Trip Creator</span>
                  </div>
                )}

                {/* {isActive && (
                  <div className="w-full flex items-center justify-center gap-1.5 rounded-lg bg-gradient-to-r from-emerald-500/10 to-emerald-600/10 px-3 py-2 text-xs font-medium text-emerald-700 border border-emerald-200/50">
                    <div className="relative w-2 h-2">
                      <div className="absolute w-full h-full rounded-full bg-emerald-500 animate-ping opacity-75" />
                      <div className="absolute w-full h-full rounded-full bg-emerald-500" />
                    </div>
                    <span>Currently Active</span>
                  </div>
                )} */}

                {tripData?.users.find((u) => u.user_id === currentUser?.user_id)
                  ?.creator &&
                  !user.creator && (
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        onRemoveUser(user);
                      }}
                      className="w-full flex items-center justify-center gap-1.5 rounded-lg bg-gradient-to-r from-red-500/10 to-red-600/10 px-3 py-2 text-xs font-medium text-red-700 hover:from-red-500/20 hover:to-red-600/20 transition-all duration-200 border border-red-200/50"
                    >
                      <Trash className="w-3.5 h-3.5" />
                      Remove from Trip
                    </button>
                  )}
              </div>
            }
            placement="bottom"
            size="lg"
            radius="lg"
            showArrow={true}
            closeDelay={200}
            offset={8}
          >
            <div
              className={`relative w-8 h-8 2xl:w-10 2xl:h-10 rounded-full ring-2 ring-white ${
                index !== 0 ? "-ml-2" : ""
              }`}
              style={{ zIndex: sortedUsers.length - index }}
            >
              <AvatarImage
                src={user.profile_image}
                firstName={user.first_name}
                lastName={user.last_name}
                className="w-full h-full object-cover"
              />
              {isActive && user.user_id === currentUser?.user_id && (
                <div className="absolute -bottom-1 -right-1 2xl:-bottom-1.5 2xl:-right-1.5 w-3.5 h-3.5 2xl:w-4 2xl:h-4 bg-emerald-500 border-2 border-white rounded-full" />
              )}
            </div>
          </Tooltip>
        );
      })}

      <Dialog
        open={isDialogOpen}
        onOpenChange={(open) => {
          if (!open) {
            setSearchQuery("");
            setSearchResults([]);
            setIsDialogOpen(false);
          }
        }}
      >
        <DialogTrigger asChild onClick={handleOpen}>
          <button
            className="w-9 h-9 2xl:w-11 2xl:h-11 rounded-full bg-white flex items-center justify-center ml-3 hover:bg-neutral-50 ring-1 ring-neutral-200 transition-colors duration-200
            focus:outline-none"
            style={{
              zIndex: tripData?.users.length ? tripData?.users.length + 1 : 1,
            }}
          >
            <UserPlusIcon className="w-4 h-4 2xl:w-5 2xl:h-5 text-neutral-800" />
          </button>
        </DialogTrigger>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Share Your Trip</DialogTitle>
            <DialogDescription className="text-sm text-neutral-600 font-light">
              Add friends to collaborate on your trip planning
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 pt-2">
            {/* Search Users Section */}
            <div className="space-y-2">
              <Label>Search Friends</Label>
              <div className="relative" ref={searchContainerRef}>
                <div className="relative h-10 w-full group">
                  <UsersRound className="absolute w-4 h-4 left-3 top-1/2 transform -translate-y-1/2 text-neutral-500 z-10 group-focus-within:text-indigo-800 transition-colors" />
                  <Input
                    type="text"
                    placeholder="Search friends by name or email"
                    className="text-sm pl-10 pr-3 py-2 rounded-lg w-full font-light placeholder:font-light focus:border-neutral-300"
                    value={searchQuery}
                    onChange={handleSearchChange}
                  />
                </div>

                {/* Repositioned search results container */}
                {(searchResults.length > 0 ||
                  (searchQuery.length >= 3 && isLoading)) && (
                  <div className="absolute left-0 right-0 top-full mt-2 bg-white rounded-lg border border-gray-200 shadow-lg z-50">
                    <div className="max-h-[200px] overflow-y-auto py-1">
                      {searchResultsContent}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Quick Picks Section */}
            {connections && connections.length > 0 && (
              <div className="space-y-1">
                <Label>Quick Picks</Label>
                <div className="flex gap-2 overflow-x-auto py-2">
                  {connectionsContent}
                </div>
              </div>
            )}
            {/* Button to share trip */}
            <div className="flex justify-center items-center flex-col space-y-3 mt-4">
              <Button
                className={`w-full text-center bg-indigo-600 hover:bg-indigo-700 rounded-xl ${
                  selectedFriends.size === 0 ? "opacity-50 cursor-default" : ""
                }`}
                onClick={handleShareTrip}
                disabled={selectedFriends.size === 0 || isSharing}
              >
                {isSharing ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <span>Share Trip</span>
                )}
              </Button>
              {selectedFriends.size > 0 && (
                <p className="text-xs text-neutral-500 font-light">
                  Sharing with {selectedFriends.size} friend
                  {selectedFriends.size > 1 ? "s" : ""}
                </p>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
});

export default TripUsers;
